import * as L from 'leaflet';

interface ContourLayerOptions extends L.TileLayerOptions {
  transparent: boolean;
  labelColor?: string;
  labelFontSize?: string;
  contourColor?: string;
}

export class NMTContourLayer extends L.TileLayer {
  myoptions: ContourLayerOptions;

  constructor(urlTemplate: string, options: ContourLayerOptions) {
    super(urlTemplate, options);
    this.myoptions = {
      transparent: true,
      labelColor: 'red',
      labelFontSize: '12px',
      contourColor: 'rgba(0, 0, 0, 1)',
      ...options
    }
  }

  createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const canvas = L.DomUtil.create('canvas', 'leaflet-tile') as HTMLCanvasElement;
    const zoom = window.mainmap.getZoom();
    if (zoom <= 17) {
      canvas.width = 256;
      canvas.height = 256;
    } else if (zoom == 18) {
      canvas.width = 512;
      canvas.height = 512;
    } else if (zoom == 19) {
      canvas.width = 1024;
      canvas.height = 1024;
    } else if (zoom == 20) {
      canvas.width = 2048;
      canvas.height = 2048;
    } else {
      canvas.width = 2048;
      canvas.height = 2048;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const prom = this.fetchNMTFile(coords);
    const coords_right = { z: coords.z, x: coords.x + 1, y: coords.y } as L.Coords;
    const coords_down = { z: coords.z, x: coords.x, y: coords.y + 1 } as L.Coords;
    const prom_right = this.fetchNMTFile(coords_right);
    const prom_down = this.fetchNMTFile(coords_down);

    let data: Uint16Array | null = null;
    let data_right: Uint16Array | null = null;
    let data_down: Uint16Array | null = null;
    Promise.allSettled([prom, prom_right, prom_down]).then((results) => {
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          if (index == 0) {
            data = result.value;
          }
          if (index == 1) {
            data_right = result.value;
          }
          if (index == 2) {
            data_down = result.value;
          }
        }
        if (result.status == "rejected") {
          if (index == 0) {
            done(Error('Error'), canvas);
          }
        }
      });
      if (data) {
        this.render(data, data_right, data_down, ctx);
        done(undefined, canvas);
      } else {
        done(Error('ERROR'), canvas);
      }
    })

    return canvas;
  }

  private async fetchNMTFile(coords: L.Coords) {
    const url = this.getTileUrl(coords);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error downloading NMT data');
    } else {
      const buffer = await response.arrayBuffer();
      const data = new Uint16Array(buffer); // 256x256
      return data;
    }
  }

  private scaleNearest(data: Uint16Array, origSize: number, size: number): Uint16Array {
    if (!data) {
      return new Uint16Array(size * size);
    }
    if (origSize === size) return data;
    const up = new Uint16Array(size * size);
    const ratio = size / origSize;
    for (let y = 0; y < size; y++) {
      const sy = Math.min(origSize - 1, Math.floor(y / ratio));
      const srcRow = sy * origSize;
      const dstRow = y * size;
      for (let x = 0; x < size; x++) {
        const sx = Math.min(origSize - 1, Math.floor(x / ratio));
        up[dstRow + x] = data[srcRow + sx] || 0;
      }
    }
    return up;
  }

  private render(data: Uint16Array, data_right: Uint16Array | null, data_down: Uint16Array | null, ctx: CanvasRenderingContext2D) {
    const zoom = window.mainmap.getZoom();
    let step = 0;
    let size = 0;
    if (zoom <= 17) {
      size = 256;
      step = 1000;
    } else if (zoom == 18) {
      size = 512;
      step = 500;
    } else if (zoom == 19) {
      size = 1024;
      step = 200;
    } else if (zoom == 20) {
      size = 2048;
      step = 100;
    } else {
      size = 2048;
      step = 100;
    }

    // 1. Tło
    if (!this.myoptions.transparent) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
    }

    // Jeśli rozmiar źródłowych danych różni się od oczekiwanego `size`, przeskaluj (nearest-neighbor)
    const origSize = Math.round(Math.sqrt(data.length));
    let srcData: Uint16Array = data;
    if (origSize !== size) {
      srcData = this.scaleNearest(data, origSize, size);
    }

    // Jeśli sąsiednie kafelki istnieją, również je przeskaluj tak samo
    const scaled_right = data_right ? this.scaleNearest(data_right, Math.round(Math.sqrt(data_right.length)), size) : null;
    const scaled_down = data_down ? this.scaleNearest(data_down, Math.round(Math.sqrt(data_down.length)), size) : null;

    const contourPoints = new Uint8Array(size * size);
    const contourValues = new Int32Array(size * size);

    // 2. Wykrywanie punktów poziomic (indeksowanie płaskie) — uwzględniamy sąsiadów z innych kafelków
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = y * size + x;
        const h = srcData[idx];
        let isContour = false;
        let val = 0;

        // Sprawdzamy sąsiada z prawej (z wewnątrz kafelka lub z kafelka po prawej)
        let rightVal: number | null = null;
        if (x < size - 1) {
          rightVal = srcData[y * size + (x + 1)];
        } else if (scaled_right) {
          // porównaj z lewą kolumną kafelka z prawej
          rightVal = scaled_right[y * size + 0];
        }

        if (rightVal !== null && Math.floor(h / step) !== Math.floor(rightVal / step)) {
          isContour = true;
          val = Math.max(Math.floor(h / step), Math.floor(rightVal / step)) * step;
        }

        // Sprawdzamy sąsiada z dołu (tylko jeśli z prawej nic nie znaleźliśmy)
        if (!isContour) {
          let bottomVal: number | null = null;
          if (y < size - 1) {
            bottomVal = srcData[(y + 1) * size + x];
          } else if (scaled_down) {
            // porównaj z górną linią kafelka poniżej
            bottomVal = scaled_down[0 * size + x];
          }

          if (bottomVal !== null && Math.floor(h / step) !== Math.floor(bottomVal / step)) {
            isContour = true;
            val = Math.max(Math.floor(h / step), Math.floor(bottomVal / step)) * step;
          }
        }

        if (isContour) {
          contourPoints[idx] = 1;
          contourValues[idx] = val;
        }
      }
    }

    // 3. Rysowanie linii na ImageData
    const imgData = ctx.createImageData(size, size);
    // Determine contour RGBA bytes from myoptions.contourColor
    const parseRgba = (s: string | undefined) => {
      const def = [0, 0, 0, 255];
      if (!s || typeof s !== 'string') return def;
      const rgba = s.replace(/\s+/g, '');
      const m = rgba.match(/rgba?\((\d+),(\d+),(\d+)(?:,(0|1|0?\.\d+|\d+))?\)/i);
      if (m) {
        const r = Math.max(0, Math.min(255, parseInt(m[1], 10) || 0));
        const g = Math.max(0, Math.min(255, parseInt(m[2], 10) || 0));
        const b = Math.max(0, Math.min(255, parseInt(m[3], 10) || 0));
        let a = 255;
        if (m[4] !== undefined) {
          const aRaw = parseFloat(m[4]);
          if (aRaw <= 1) a = Math.round(aRaw * 255);
          else a = Math.round(Math.max(0, Math.min(255, aRaw)));
        }
        return [r, g, b, a];
      }
      return def;
    };
    const contourColorBytes = parseRgba(this.myoptions.contourColor as string);
    for (let i = 0; i < size * size; i++) {
      const px = i * 4;
      if (contourPoints[i]) {
        imgData.data[px] = contourColorBytes[0];
        imgData.data[px + 1] = contourColorBytes[1];
        imgData.data[px + 2] = contourColorBytes[2];
        imgData.data[px + 3] = contourColorBytes[3];
      } else {
        imgData.data[px] = 255; imgData.data[px + 1] = 255; imgData.data[px + 2] = 255; imgData.data[px + 3] = 0;
      }
    }
    ctx.putImageData(imgData, 0, 0);

    // 4. Podpisywanie spójnych segmentów — ograniczamy rozprzestrzenianie flood-fill do zasięgu etykiety
    const visited = new Uint8Array(size * size);
    ctx.font = `bold ${this.myoptions.labelFontSize} sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = "white";
    ctx.lineWidth = 3;

    const labelThreshold = 400; // Manhattan distance threshold in pixels
    const padding = 12;

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = y * size + x;

        if (contourPoints[idx] && !visited[idx]) {
          const seedX = x;
          const seedY = y;

          // Place label at the seed point (this region will cover nearby pixels up to labelThreshold)
          const currentHeight = contourValues[idx];
          const safeX = Math.max(padding, Math.min(size - padding, seedX));
          const safeY = Math.max(padding, Math.min(size - padding, seedY));
          ctx.strokeText(String(currentHeight / 100), safeX, safeY);
          ctx.fillStyle = this.myoptions.labelColor ?? 'red';
          ctx.fillText(String(currentHeight / 100), safeX, safeY);

          // Flood-fill limited by Manhattan distance from the seed
          const stack: Array<[number, number]> = [[seedX, seedY]];
          visited[idx] = 1;
          while (stack.length > 0) {
            const [cx, cy] = stack.pop();
            const cIdx = cy * size + cx;
            if (cx >= 0 && cx < size && cy >= 0 && cy < size && contourPoints[cIdx]) {
              visited[cIdx] = 1;
              const neighbors = [[cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1], [cx + 1, cy + 1], [cx - 1, cy - 1], [cx + 1, cy - 1], [cx - 1, cy + 1]];
              for (const [nx, ny] of neighbors) {
                if (nx >= 0 && nx < size && ny >= 0 && ny < size) {
                  const nIdx = ny * size + nx;
                  if (!visited[nIdx] && contourPoints[nIdx]) {
                    const dist = Math.abs(nx - seedX) + Math.abs(ny - seedY);
                    if (dist <= labelThreshold) {
                      visited[nIdx] = 1;
                      stack.push([nx, ny]);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // Tworzymy overlay z obecnego canvas (z etykietami) i nadpisujemy tylko te pixele, gdzie mamy kontur — to zachowa etykiety, akontury będą nad nimi.
    const overlay = ctx.getImageData(0, 0, size, size);
    const total = size * size;
    for (let i = 0; i < total; i++) {
      if (contourPoints[i]) {
        const px = i * 4;
        overlay.data[px] = contourColorBytes[0];
        overlay.data[px + 1] = contourColorBytes[1];
        overlay.data[px + 2] = contourColorBytes[2];
        overlay.data[px + 3] = contourColorBytes[3];
      }
    }
    ctx.putImageData(overlay, 0, 0);
  }

}